import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';

import ReportsService from '../../../../services/reports.service';
import { useReactQuery } from '../../../../hooks';
import AddIcon from '@mui/icons-material/Add';
import { CONST_API_NODEJS_URL } from '../../../../constants';
import { IconLogoPrism } from '../../../../assets/images/icons'
import { nodeJsonFunc } from '../../components/pptDrawer/exportToPptWithNode';

import { SegmentOverview, ReportSegmentAndSegmentOfSegmentV2, ReportDeepDiveNewV2, ReportSegmentV2 } from '../../components'

import Tabs from './tabs'
import LeftFieldsPanel from './lhs';
import RightCanvasPanel from './filters';
import { InstructionText, CanvasContainer, EmptyIconCard, EmptyStateSvg, EmptyStateContainer, EmptyStateIconBadge, } from './filters/style';

import {
  OuterWrapper,
  Workspace,
  PrismHeading,
  SelectionPanelHeader,
  WorkspaceBody,
  ResultData,
  CanvasOuterContainer
} from './style';
import { getObjectAsArray, sortByKey } from '../../../../util';
import { MuiButton } from '../../../../components';
import InsufficientData from './insufficientData';

const INITIAL_FIELDS = [
  {
    FactorName: 'Brands',
    FactorInternalName: 'Brands',
    isSelected: false,
    isViewEnabled: true,
    isFilterEnabled: true,
    isSliceEnabled: true,
    options: ['Brand A', 'Brand B', 'Brand C'],
  },
  {
    FactorName: 'Past Ownership',
    FactorInternalName: 'Past Ownership',
    isSelected: false,
    isViewEnabled: false,
    isFilterEnabled: true,
    isSliceEnabled: true,
    options: ['1st Hand', '2nd Hand'],
  },
];

const ReportPrism = (props) => {
  const useReactQueryObject = {
    payloadDetails: {
      queryId: [
        props.InsightsElementType,
        props.InsightsElementId,
      ],
      urlFunc: ReportsService.getPrismFactors,
      payload: {
        insightsElementId: props.InsightsElementId,
      },
    },
  };

  const { data, isSuccess } =
    useReactQuery(useReactQueryObject);

  /**
   * fields contains the complete factor information.
   *
   * FactorName:
   *   Display label.
   *
   * FactorInternalName:
   *   Internal identifier.
   */
  const [fields, setFields] =
    useState(INITIAL_FIELDS);

  /**
   * IMPORTANT:
   *
   * These arrays contain FactorInternalName only.
   */
  const [views, setViews] = useState([]);
  const [filters, setFilters] = useState([]);
  const [slices, setSlices] = useState([]);
  const [prismResultData, setPrismResultData] = useState([]);
  const [filterLoading, setFilterLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  /**
   * Object key = FactorInternalName
   *
   * Example:
   *
   * {
   *   "family_size": ["Small", "Medium"]
   * }
   */
  const [filterSelections, setFilterSelections] =
    useState({});

  const [errorMessage, setErrorMessage] =
    useState('');

  /**
   * Contains FactorInternalName values.
   */
  const [invalidFilters, setInvalidFilters] =
    useState([]);

  /**
   * --------------------------------------------------------------------------
   * HELPERS
   * --------------------------------------------------------------------------
   */

  /**
   * Get FactorInternalName.
   *
   * FactorInternalName is the internal identity.
   */
  const getFactorInternalName = (field) => {
    if (!field) {
      return '';
    }

    return (
      field.FactorInternalName ??
      field.factorInternalName ??
      ''
    );
  };

  /**
   * Get FactorName.
   *
   * FactorName is only used for display.
   */
  const getFactorName = (field) => {
    if (!field) {
      return '';
    }

    return (
      field.FactorName ??
      field.factorName ??
      ''
    );
  };

  /**
   * Get FactorType.
   *
   * FactorType is only used for display.
   */
  const getFactorType = (field) => {
    if (!field) {
      return '';
    }

    return (
      field.FactorType ??
      field.factorType ??
      ''
    );
  };

  /**
   * Find a field using FactorInternalName.
   */
  const getFieldByInternalName = (
    FactorInternalName
  ) => {
    return fields.find(
      (field) =>
        getFactorInternalName(field) ===
        FactorInternalName
    );
  };

  /**
   * Get display label from FactorInternalName.
   */
  const getDisplayNameByInternalName = (
    FactorInternalName
  ) => {
    const field =
      getFieldByInternalName(
        FactorInternalName
      );

    return (
      getFactorName(field) ||
      FactorInternalName
    );
  };

  /**
   * --------------------------------------------------------------------------
   * FIELD SELECTION
   * --------------------------------------------------------------------------
   */

  /**
   * Update field selection using FactorInternalName.
   */
  const updateFieldSelectionState = (
    FactorInternalName,
    isSelected
  ) => {
    setFields((prevFields) =>
      prevFields.map((field) =>
        getFactorInternalName(field) ===
        FactorInternalName
          ? {
              ...field,
              isSelected,
            }
          : field
      )
    );
  };

  /**
   * --------------------------------------------------------------------------
   * DRAG START
   * --------------------------------------------------------------------------
   */

  const handleDragStart = (
    field,
    event
  ) => {
    if (!field || field.isSelected) {
      event.preventDefault();
      return;
    }

    const FactorName =
      getFactorName(field);

    const FactorInternalName =
      getFactorInternalName(field);

    if (!FactorInternalName) {
      event.preventDefault();
      return;
    }

    event.dataTransfer.setData(
      'application/json',
      JSON.stringify({
        /**
         * Display label.
         */
        FactorName,

        /**
         * Internal identifier.
         */
        FactorInternalName,

        IsFilterEnabled:
          field.isFilterEnabled ??
          field.IsFilterEnabled ??
          true,

        IsSliceEnabled:
          field.isSliceEnabled ??
          field.IsSliceEnabled ??
          true,

        IsViewEnabled:
          field.isViewEnabled ??
          field.IsViewEnabled ??
          true,

        options:
          field.options ??
          field.FactorOptions ??
          [],
      })
    );
  };

  /**
   * --------------------------------------------------------------------------
   * DROP FIELD
   * --------------------------------------------------------------------------
   */

  const handleDropField = (
    field,
    target
  ) => {
    if (!field) {
      return;
    }

    const FactorInternalName =
      getFactorInternalName(field);

    if (!FactorInternalName) {
      return;
    }

    const isFilterEnabled =
      field.IsFilterEnabled ??
      field.isFilterEnabled ??
      true;

    const isSliceEnabled =
      field.IsSliceEnabled ??
      field.isSliceEnabled ??
      true;

    const isViewEnabled =
      field.IsViewEnabled ??
      field.isViewEnabled ??
      true;

    /**
     * Check target permissions.
     */
    if (
      target === 'filter' &&
      !isFilterEnabled
    ) {
      return;
    }

    if (
      target === 'slice' &&
      !isSliceEnabled
    ) {
      return;
    }

    if (
      target === 'view' &&
      !isViewEnabled
    ) {
      return;
    }

    /**
     * Filters and slices require at least
     * one View.
     */
    if (
      target !== 'view' &&
      views.length === 0
    ) {
      return;
    }

    /**
     * Mark as selected.
     */
    updateFieldSelectionState(
      FactorInternalName,
      true
    );

    /**
     * Add to View.
     */
    if (target === 'view') {
      setViews((prev) =>
        prev.includes(
          FactorInternalName
        )
          ? prev
          : [
              ...prev,
              FactorInternalName,
            ]
      );

      return;
    }

    /**
     * Add to Filter.
     */
    if (target === 'filter') {
      setFilters((prev) =>
        prev.includes(
          FactorInternalName
        )
          ? prev
          : [
              ...prev,
              FactorInternalName,
            ]
      );

      setFilterSelections((prev) => ({
        ...prev,
        [FactorInternalName]:
          prev[FactorInternalName] || [],
      }));

      return;
    }

    /**
     * Add to Slice.
     */
    if (target === 'slice') {
      setSlices((prev) =>
        prev.includes(
          FactorInternalName
        )
          ? prev
          : [
              ...prev,
              FactorInternalName,
            ]
      );
    }
  };

  /**
   * --------------------------------------------------------------------------
   * FILTER OPTION TOGGLE
   * --------------------------------------------------------------------------
   */

  const handleFilterOptionToggle = (
    FactorInternalName,
    optionValue
  ) => {
    setErrorMessage('');

    setInvalidFilters((prev) =>
      prev.filter(
        (item) =>
          item !== FactorInternalName
      )
    );

    setFilterSelections((prev) => {
      const current =
        prev[FactorInternalName] || [];

      const updated =
        current.includes(optionValue)
          ? current.filter(
              (item) =>
                item !== optionValue
            )
          : [
              ...current,
              optionValue,
            ];

      return {
        ...prev,
        [FactorInternalName]: updated,
      };
    });
  };

  /**
   * --------------------------------------------------------------------------
   * REMOVE VIEW
   * --------------------------------------------------------------------------
   */

  const handleRemoveView = (
    FactorInternalName
  ) => {
    setViews((prev) =>
      prev.filter(
        (item) =>
          item !== FactorInternalName
      )
    );

    /**
     * Only unselect when the field is not
     * being used anywhere else.
     */
    if (
      !filters.includes(
        FactorInternalName
      ) &&
      !slices.includes(
        FactorInternalName
      )
    ) {
      updateFieldSelectionState(
        FactorInternalName,
        false
      );
    }
  };

  /**
   * --------------------------------------------------------------------------
   * REMOVE FILTER
   * --------------------------------------------------------------------------
   */

  const handleRemoveFilter = (
    FactorInternalName
  ) => {
    setFilters((prev) =>
      prev.filter(
        (item) =>
          item !== FactorInternalName
      )
    );

    setFilterSelections((prev) => {
      const updated = {
        ...prev,
      };

      delete updated[
        FactorInternalName
      ];

      return updated;
    });

    setInvalidFilters((prev) =>
      prev.filter(
        (item) =>
          item !== FactorInternalName
      )
    );

    /**
     * Only unselect when not used in View
     * or Slice.
     */
    if (
      !views.includes(
        FactorInternalName
      ) &&
      !slices.includes(
        FactorInternalName
      )
    ) {
      updateFieldSelectionState(
        FactorInternalName,
        false
      );
    }
  };

  /**
   * --------------------------------------------------------------------------
   * REMOVE SLICE
   * --------------------------------------------------------------------------
   */

  const handleRemoveSlice = (
    FactorInternalName
  ) => {
    setSlices((prev) =>
      prev.filter(
        (item) =>
          item !== FactorInternalName
      )
    );

    /**
     * Only unselect when not used in View
     * or Filter.
     */
    if (
      !views.includes(
        FactorInternalName
      ) &&
      !filters.includes(
        FactorInternalName
      )
    ) {
      updateFieldSelectionState(
        FactorInternalName,
        false
      );
    }
  };

  /**
   * --------------------------------------------------------------------------
   * BUILD PRISM PAYLOAD
   * --------------------------------------------------------------------------
   */

  const buildPrismPayload = () => {
    return {
      insightsElementId:
        props.InsightsElementId,

      /**
       * ----------------------------------------------------------------------
       * VIEW FACTORS
       * ----------------------------------------------------------------------
       */

      viewFactors: views.map(
        (FactorInternalName) => {
          const field =
            getFieldByInternalName(
              FactorInternalName
            );

          return {
            PrismViewFactor: {
              /**
               * Display label.
               */
              ViewFactorName:
                getFactorName(field),

              /**
               * Internal identifier.
               */
              ViewFactorInternalName:
                FactorInternalName,

              ViewFactorType:
                getFactorType(field),
            },
          };
        }
      ),

      /**
       * ----------------------------------------------------------------------
       * FILTER FACTORS
       * ----------------------------------------------------------------------
       */

      filterFactors: filters.map((FactorInternalName) => {
        const field = getFieldByInternalName(FactorInternalName);

        return {
          PrismFilterFactor: {
            FilterFactorName: getFactorName(field),

            FilterFactorInternalName: FactorInternalName,

            FilterFactorType: getFactorType(field),

            FilterFactorValues: (
              filterSelections[FactorInternalName] || []
            ).map((value) => ({
              string: value,
            })),
          },
        };
      }),

      /**
       * ----------------------------------------------------------------------
       * SPLIT FACTORS
       * ----------------------------------------------------------------------
       */

      splitFactors: slices.map(
        (FactorInternalName) => {
          const field =
            getFieldByInternalName(
              FactorInternalName
            );

          return {
            PrismSplitFactor: {
              /**
               * Display label.
               */
              SplitFactorName:
                getFactorName(field),

              /**
               * Internal identifier.
               */
              SplitFactorInternalName:
                FactorInternalName,
            },
          };
        }
      ),
    };
  };

  const getPrismTopContentHtml = (prismData) => {
    const data1 = prismData?.Heading || '';
    const sampleData = prismData?.PrismOutputSamples?.[0] || null;

    // Clean HTML tags from Heading
    const cleanFilterText =
      typeof data1 === 'string'
        ? data1.replace(/<\/?p>/g, '')
        : '';

    // Remove "Filter:" if it already exists
    let filterContent = cleanFilterText;
    if (cleanFilterText.startsWith('Filter:')) {
      filterContent = cleanFilterText.replace('Filter:', '').trim();
    }

    const baseText = sampleData
      ? `${sampleData.SampleName || ''} ${sampleData.SampleValue || ''}`.trim()
      : '';

    return `<div><ul>${filterContent ? `<li>Filter: ${filterContent}</li>` : ''}${baseText ? `<li>${baseText}</li>` : ''}</ul></div>`;
  };

  /**
   * --------------------------------------------------------------------------
   * GENERATE GRAPH
   * --------------------------------------------------------------------------
   */
  const handleGenerateGraph = () => {
    /**
     * Find filters that don't have any selected options.
     *
     * filters contains FactorInternalName.
     */
    const emptyOptionFilters = filters.filter((factorInternalName) => {
      const selections = filterSelections[factorInternalName];

      return !selections || selections.length === 0;
    });

    /**
     * Validate filters.
     */
    if (emptyOptionFilters.length > 0) {
      setInvalidFilters(emptyOptionFilters);

      /**
       * Convert internal IDs to display labels
       * only for the error message.
       */
      const displayNames = emptyOptionFilters.map((factorInternalName) =>
        getDisplayNameByInternalName(factorInternalName)
      );

      let formattedNames = '';

      if (displayNames.length === 1) {
        formattedNames = displayNames[0];
      } else if (displayNames.length === 2) {
        formattedNames = `${displayNames[0]} and ${displayNames[1]}`;
      } else {
        const last = displayNames[displayNames.length - 1];
        const rest = displayNames.slice(0, -1).join(', ');

        formattedNames = `${rest}, and ${last}`;
      }

      setErrorMessage(
        `Select at least one option for ${formattedNames} or remove them to continue.`
      );

      return;
    }

    /**
     * Clear previous validation errors.
     */
    setErrorMessage('');
    setInvalidFilters([]);

    /**
     * Runtime representation.
     *
     * Internal state uses FactorInternalName.
     * FactorName is included only for readable/debug information.
     */
    const runtimeObject = {
      views: views.map((factorInternalName) => ({
        FactorInternalName: factorInternalName,
        FactorName: getDisplayNameByInternalName(factorInternalName),
      })),

      slices: slices.map((factorInternalName) => ({
        FactorInternalName: factorInternalName,
        FactorName: getDisplayNameByInternalName(factorInternalName),
      })),

      filters: filters.map((factorInternalName) => ({
        FactorInternalName: factorInternalName,
        FactorName: getDisplayNameByInternalName(factorInternalName),
        selectedOptions: filterSelections[factorInternalName] || [],
      })),

      timestamp: new Date().toISOString(),
    };

    /**
     * Actual Prism API payload.
     */
    const obj = buildPrismPayload();

    setFilterLoading(true);

    ReportsService.getPrismOutput(obj)
      .then((resp) => {
        const resp1 = (Array.isArray(resp) ? resp : []).map((d) => {
          d.InsightsData = getPrismTopContentHtml(d)
          return d
        })

        // HOW TO REPLICATE THIS ISSUE: Select 4-5 filters

        const viewFactors = obj?.viewFactors || [];

        const tabsObj = (Array.isArray(resp1) ? resp1 : []).map(
          (tab, index) => {
            const isNil = tab?.$?.['xsi:nil'] === 'true';

            const itemObj = isNil && viewFactors[index]
              ? viewFactors[index]
              : tab;

            if(itemObj?.PrismViewFactor?.ViewFactorInternalName) {
              itemObj['Heading'] = itemObj['Title'] = itemObj.PrismViewFactor.ViewFactorName
              itemObj['index'] = index
              itemObj['InsightsElementType'] = "InsufficientData"
              itemObj['SegmentAnalysisElements'] = {SegmentAnalysisElement: []}
            }

            return itemObj
          }
        );

        setPrismResultData(tabsObj);
      })
      .catch((error) => {
        console.error('Failed to generate Prism graph:', error);

        setErrorMessage(
          'Unable to generate the graph. Please try again.'
        );
      })
      .finally(() => {
        setFilterLoading(false);
      });
  };

  const handleExportToPpt = async () => {
    if (
      !Array.isArray(prismResultData) ||
      prismResultData.length === 0 ||
      isExporting
    ) {
      return;
    }

    setIsExporting(true);
    setExportProgress(0);

    try {
      const firstResult = prismResultData[0] || {};
      const studyTitle = String(
        firstResult.StudyTitle ||
        firstResult.Title ||
        'Prism Report'
      );

      const fileName = `Prism_${studyTitle.replace(/[^a-z0-9]/gi, '')}_${Date.now()}`;

      // Build PPT payload
      const payload = [];

      for (let i = 0; i < prismResultData.length; i++) {
        const d = prismResultData[i];

        const headingInsightElement = document.getElementById(
          `prismInsights__${d.InsightsElementType}__${i}`
        );

        let insightsPPT = null;

        if (headingInsightElement?.childNodes?.length) {
          insightsPPT = await nodeJsonFunc(
            headingInsightElement.childNodes,
            'jsonObject_headingInsights'
          );
        }

        payload.push({
          EntityId: null,
          Heading: d.Heading,
          Insights: d.InsightsData,
          InsightsElementId: null,
          InsightsElementOrder: i,
          InsightsElementType: d.InsightsElementType,

          // Use the result from nodeJsonFunc here
          InsightsPPT: insightsPPT,

          Observations: {},
          ParentTitle: '',
          StudyId: 'id',
          TabId: null,
          Title: d.Title,
          base64: null,
          id: null,
          isAppendices: false,
          isFreezed: false,
          isProgression: false,
          pptData: getServerData(d),
        });
      }

      const exportPayload = {
        fileName,
        method: '',
        props: {
          title: studyTitle,
          subTitle: 'Prism',
          lastUpdated: new Date().toISOString(),
          studyDetails: {
            studyTitle,
            breadCrumb: 'Prism',
            subTitle: 'Prism',
          },
        },
        payload,
      };

      const filteredPayload = exportPayload
      filteredPayload.payload = filteredPayload.payload.filter(item => item.InsightsElementType !== 'InsufficientData')

      const reportResponse = await fetch(`${CONST_API_NODEJS_URL}/reports`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(filteredPayload),
      });

      if (!reportResponse.ok) {
        throw new Error(`PPT export failed: ${reportResponse.status}`);
      }

      let statusData;

      while (!statusData?.fileExists) {
        await new Promise((resolve) => setTimeout(resolve, 3000));

        const statusResponse = await fetch(
          `${CONST_API_NODEJS_URL}/status`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ fileName }),
          }
        );

        if (!statusResponse.ok) {
          throw new Error(`PPT status failed: ${statusResponse.status}`);
        }

        statusData = await statusResponse.json();

        const processLog = statusData?.processLog || {};
        const completed = processLog.completed?.length || 0;
        const pending = processLog.pending?.length || 0;
        const errors = processLog.errors?.length || 0;

        const total = completed + pending + errors;

        const percentage =
          statusData?.percentage ??
          (total
            ? ((completed + errors) / total) * 100
            : 0);

        setExportProgress(Math.min(99, Math.round(percentage)));

        if (
          ['failed', 'error', 'completed_with_errors'].includes(
            statusData.status
          )
        ) {
          throw new Error('PPT export completed with errors');
        }
      }

      const downloadResponse = await fetch(
        `${CONST_API_NODEJS_URL}${statusData.downloadUrl}.pptx`
      );

      if (!downloadResponse.ok) {
        throw new Error('PPT download failed');
      }

      const blob = await downloadResponse.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = statusData.fileName || `${fileName}.pptx`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      URL.revokeObjectURL(blobUrl);

      setExportProgress(100);
    } catch (error) {
      console.error('Error exporting Prism to PPT:', error);
      setExportProgress(0);
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * --------------------------------------------------------------------------
   * MAP API FACTORS
   * --------------------------------------------------------------------------
   */

  useEffect(() => {
    if (
      isSuccess &&
      Array.isArray(data) &&
      data.length
    ) {
      // const fData  = data.map(d => ({...d, FactorName: d.FactorName + ' displyNm'}))
      const fData  = data.map(d => ({...d, FactorName: d.FactorName}))

      setFields((prevFields) =>
        fData.map((newItem) => {
          const FactorInternalName =
            getFactorInternalName(
              newItem
            );

          const existing =
            prevFields.find(
              (field) =>
                getFactorInternalName(
                  field
                ) ===
                FactorInternalName
            );

          return {
            ...newItem,

            /**
             * Display label.
             */
            FactorName:
              getFactorName(newItem),

            /**
             * Internal identity.
             */
            FactorInternalName,

            isSelected:
              existing?.isSelected ??
              false,
          };
        })
      );
    }
  }, [isSuccess, data]);

  /**
   * --------------------------------------------------------------------------
   * CLEAR ALL
   * --------------------------------------------------------------------------
   */

  const handleClearAll = (
    type
  ) => {
    /**
     * ----------------------------------------------------------------------
     * CLEAR FILTERS
     * ----------------------------------------------------------------------
     */

    if (type === 'filter') {
      filters.forEach(
        (FactorInternalName) => {
          if (
            !views.includes(
              FactorInternalName
            ) &&
            !slices.includes(
              FactorInternalName
            )
          ) {
            updateFieldSelectionState(
              FactorInternalName,
              false
            );
          }
        }
      );

      setFilters([]);
      setFilterSelections({});
      setInvalidFilters([]);
      setErrorMessage('');

      return;
    }

    /**
     * ----------------------------------------------------------------------
     * CLEAR VIEWS
     * ----------------------------------------------------------------------
     */

    if (type === 'view') {
      views.forEach(
        (FactorInternalName) => {
          if (
            !filters.includes(
              FactorInternalName
            ) &&
            !slices.includes(
              FactorInternalName
            )
          ) {
            updateFieldSelectionState(
              FactorInternalName,
              false
            );
          }
        }
      );

      setViews([]);

      return;
    }

    /**
     * ----------------------------------------------------------------------
     * CLEAR SLICES
     * ----------------------------------------------------------------------
     */

    if (type === 'slice') {
      slices.forEach(
        (FactorInternalName) => {
          if (
            !views.includes(
              FactorInternalName
            ) &&
            !filters.includes(
              FactorInternalName
            )
          ) {
            updateFieldSelectionState(
              FactorInternalName,
              false
            );
          }
        }
      );

      setSlices([]);
    }
  }

  const getServerData = (item) => {
    if (item.InsightsElementType === 'SegmentOverview') {
      return getObjectAsArray(
        item?.SegmentAnalysisElements?.SegmentAnalysisElement || []
      );
    }

    if (item.InsightsElementType === 'SegmentOfSegmentOverview') {
      return getObjectAsArray(
        item?.SegmentofSegmentOverviewElements?.SegmentOfSegmentOverviewElement || []
      );
    }

    if (item.InsightsElementType === 'SegmentDriversOnlyAnalysis3Level') {
      return getObjectAsArray(
        item?.SegmentAnalysisElements?.SegmentAnalysisElement || []
      );
    }

    if (item.InsightsElementType === 'Drivers') {
      const d = getObjectAsArray(item?.DriverElements?.DriverElement || []);
      return sortByKey(d, 'Percent', false);
    }

    return [];
  };


  /**
   * --------------------------------------------------------------------------
   * RENDER
   * --------------------------------------------------------------------------
   */

  return (
    <OuterWrapper>
      <Workspace>
        <SelectionPanelHeader>
          <PrismHeading>
            <IconLogoPrism />
            Prism
          </PrismHeading>

          <MuiButton
            size='small'
            // startIcon={!alreadyDownloading && <IconPpt />}
            color='dark'
            variant='outlined'
            label={isExporting ? `Downloading ${exportProgress}%` : 'Export To PPT'}
            // endIcon={<ExportToPptWithNode checkIsDownloadingFunc={() => setAlreadyDownloading(true)} />}
            onClick={handleExportToPpt}
            disabled={!(prismResultData.filter(item => item.InsightsElementType !== 'InsufficientData')?.length > 0) || isExporting}
          />
        </SelectionPanelHeader>

        <WorkspaceBody>
          <LeftFieldsPanel
            fields={fields}
            views={views}
            onDragStart={
              handleDragStart
            }
            onDropToSection={(
              actionType,
              field
            ) =>
              handleDropField(
                field,
                actionType
              )
            }
          />

          <CanvasOuterContainer>
            <CanvasContainer component='section'>
              <RightCanvasPanel
                fields={fields}
                views={views}
                filters={filters}
                slices={slices}
                loading={filterLoading}
                filterSelections={
                  filterSelections
                }
                errorMessage={
                  errorMessage
                }
                invalidFilters={
                  invalidFilters
                }
                onFilterOptionToggle={
                  handleFilterOptionToggle
                }
                onRemoveView={
                  handleRemoveView
                }
                onRemoveFilter={
                  handleRemoveFilter
                }
                onRemoveSlice={
                  handleRemoveSlice
                }
                onClearAll={
                  handleClearAll
                }
                onDropField={
                  handleDropField
                }
                onGenerate={
                  handleGenerateGraph
                }
              />
        
              {/* EMPTY CANVAS / READY STATE */}
              {prismResultData?.length === 0 && <EmptyStateContainer className='section4'>
                <EmptyIconCard>
                  <EmptyStateSvg
                    width="76"
                    height="54"
                    viewBox="0 0 76 54"
                    fill="none"
                  >
                    <rect className="bar-1" x="2" y="30" width="8" height="22" rx="1.5" />
                    <rect className="bar-2" x="16" y="20" width="8" height="32" rx="1.5" />
                    <rect className="bar-3" x="30" y="10" width="8" height="42" rx="1.5" />
                    <rect className="bar-2" x="44" y="20" width="8" height="32" rx="1.5" />
                    <rect className="bar-1" x="58" y="30" width="8" height="22" rx="1.5" />
                    <line
                      className="axis-line"
                      x1="0"
                      y1="53"
                      x2="76"
                      y2="53"
                      strokeWidth="1.5"
                    />
                  </EmptyStateSvg>
        
                  <EmptyStateIconBadge>
                    <AddIcon className="badge-add-icon" />
                  </EmptyStateIconBadge>
                </EmptyIconCard>
              </EmptyStateContainer>}

              {prismResultData?.length > 0 && (
                <Tabs data={prismResultData} obj={prismResultData} isFirstTab={0} />
              )}

              {prismResultData && prismResultData?.length > 0 && <ResultData>
                {prismResultData.map((item, index) => {
                  const listType = `${item.InsightsElementType}__${index}`
                  let serverData = getServerData(item)
                  item['index'] = index
                  if(filters.length === 0){
                    item.Heading = ''
                  }

                  return (
                  <React.Fragment key={index}>
                    {serverData?.length === 0 ? <>
                      <InsufficientData header={item.Heading} />
                    </> : <>                    
                      {/* {<b>{item.InsightsElementType}</b>} */}
                      {item.InsightsElementType === 'SegmentOverview' && <SegmentOverview InsightsElementType={item.InsightsElementType} type={listType} serverData={serverData} prismData={{...item, Title: `<p>${item.Title}</p>`, Heading: `<p>${item.Heading}</p>`}} isExtraColor />}
                      {item.InsightsElementType === 'SegmentOfSegmentOverview' && <ReportSegmentAndSegmentOfSegmentV2 InsightsElementType={item.InsightsElementType} type={listType} serverData={{table: serverData}} prismData={{...item, Title: `<p>${item.Title}</p>`, Heading: `<p>${item.Heading}</p>`}} isExtraColor />}
                      {item.InsightsElementType === 'Drivers' && <ReportDeepDiveNewV2 InsightsElementType={item.InsightsElementType} type={listType} serverData={serverData} prismData={{...item, Title: `<p>${item.Title}</p>`, Heading: `<p>${item.Heading}</p>`}} isExtraColor />}
                      {item.InsightsElementType === 'SegmentDriversOnlyAnalysis3Level' && <ReportSegmentV2 InsightsElementType={item.InsightsElementType} type={listType} serverData={serverData} prismData={{...item, Title: `<p>${item.Title}</p>`, Heading: `<p>${item.Heading}</p>`}} isExtraColor />}
                    </>}
                  </React.Fragment>
                )})}
              </ResultData>}
            </CanvasContainer>

            {/* {!views.length && (
              <InstructionText isHeadline sx={{ textAlign: 'center', mt: 2, minHeight: '500px'}}>Add fields above to construct your view</InstructionText>
            )} */}

          </CanvasOuterContainer>
        </WorkspaceBody>
      </Workspace>
    </OuterWrapper>
  );
};

export default ReportPrism;